import { useEffect, useState } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import * as z from 'zod';
import { Loader2, Send } from 'lucide-react';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useToast } from '@/hooks/use-toast';
import { useReconnectErrorToast } from '@/lib/email/useReconnectErrorToast';
import { useEmailSender } from '@/hooks/useEmailSender';
import { supabase } from '@/lib/supabase';
import { captureError } from '@/lib/sentry';
import { renderCampaignTemplate } from '@/lib/email/campaign-merge';
import { buildTestFooterHtml } from '@/lib/email/test-footer';
import { SenderSelect } from './SenderSelect';

/**
 * Phase-1 "Send Test Email" dialog. Sends a single one-off email rendered from
 * the current template / wizard content, using the EXISTING client direct-send
 * path (`useEmailSender().sendEmail` with `isTest: true`). It does NOT touch the
 * Hermes campaign/queue pipeline.
 *
 * The body gets an approximate compliance footer (signature + physical address +
 * an INERT unsubscribe link → usebuildit.com). The dialog warns that the
 * unsubscribe link does not work. The real signed unsubscribe is Phase 2.
 */
interface SendTestEmailDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Raw subject, may contain {{tokens}}. */
  subject: string;
  /** Raw body HTML, may contain {{tokens}}. */
  body: string;
  workspaceId: string;
  /** Wizard pre-fills this; template editor leaves it undefined and shows the picker. */
  senderId?: string;
  /** When true (template-editor context), render the sender picker. */
  showSenderPicker?: boolean;
}

const formSchema = z.object({
  recipientName: z.string().trim().min(1, 'Recipient name is required'),
  recipientEmail: z
    .string()
    .trim()
    .min(1, 'Recipient email is required')
    .email('Enter a valid email address'),
});

type FormValues = z.infer<typeof formSchema>;

export function SendTestEmailDialog({
  open,
  onOpenChange,
  subject,
  body,
  workspaceId,
  senderId,
  showSenderPicker = false,
}: SendTestEmailDialogProps) {
  const { toast } = useToast();
  const showReconnectErrorToast = useReconnectErrorToast();
  const { providers, sendEmail } = useEmailSender();

  const [sending, setSending] = useState(false);
  // Template-editor context: locally-picked sender (defaults to first provider).
  const [pickedSenderId, setPickedSenderId] = useState<string | undefined>(undefined);
  // Sender signature + physical address, resolved the same way the wizard preview
  // and queue processor do, so the test footer matches a real send.
  const [senderExtras, setSenderExtras] = useState<{ signature: string; address: string }>({
    signature: '',
    address: '',
  });

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: { recipientName: '', recipientEmail: '' },
  });

  const resolvedSenderId = senderId ?? pickedSenderId;

  // Default the picker to the first connected account.
  useEffect(() => {
    if (showSenderPicker && !pickedSenderId && providers.length > 0) {
      setPickedSenderId(providers[0].id);
    }
  }, [showSenderPicker, pickedSenderId, providers]);

  // Resolve signature + physical address for the active sender (per-sender first,
  // then workspace fallback) — mirrors StepReviewPreview in CampaignWizardDialog.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      if (!resolvedSenderId) {
        if (!cancelled) setSenderExtras({ signature: '', address: '' });
        return;
      }
      const { data: prov } = await supabase
        .from('email_providers')
        .select('signature, physical_address, workspace_id')
        .eq('id', resolvedSenderId)
        .maybeSingle();
      let address = (prov?.physical_address || '').trim();
      if (!address && prov?.workspace_id) {
        const { data: ws } = await supabase
          .from('workspace_settings')
          .select('compliance_physical_address')
          .eq('workspace_id', prov.workspace_id)
          .maybeSingle();
        address = (ws?.compliance_physical_address || '').trim();
      }
      if (!cancelled) {
        setSenderExtras({ signature: (prov?.signature || '').trim(), address });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, resolvedSenderId]);

  const noAccounts = showSenderPicker && providers.length === 0;

  const handleSubmit = async (values: FormValues) => {
    if (!resolvedSenderId) {
      toast({
        title: 'No sender selected',
        description: 'Choose an email account to send the test from.',
        variant: 'destructive',
      });
      return;
    }

    // Defensive: only send from a real, owned, sendable account. `providers` is
    // RLS-scoped to the caller (user_id = auth.uid()), so this also rejects the
    // simulated 'test-account' id and any provider type the client can't send,
    // preventing a phantom "sent" + ledger row for an email that never went out.
    const senderProvider = providers.find((p) => p.id === resolvedSenderId);
    if (!senderProvider || (senderProvider.type !== 'gmail' && senderProvider.type !== 'outlook')) {
      toast({
        title: 'Unsupported sender',
        description: 'Test emails can only be sent from a connected Gmail or Outlook account.',
        variant: 'destructive',
      });
      return;
    }

    setSending(true);
    try {
      // 1. Rate-limit check (5 unique addresses/day, 25 per address/day).
      const { data: check, error: checkError } = await supabase.rpc('check_test_send_allowed', {
        p_workspace_id: workspaceId,
        p_recipient_email: values.recipientEmail,
      });
      if (checkError) throw checkError;
      const allowed = (check as { allowed?: boolean } | null)?.allowed === true;
      if (!allowed) {
        const reason =
          (check as { reason?: string } | null)?.reason ??
          'Test-email limit reached. Try again tomorrow.';
        toast({ title: 'Limit reached', description: reason, variant: 'destructive' });
        setSending(false);
        return;
      }

      // 2. Render merge fields against the synthetic test recipient.
      const mergeRecipient = {
        first_name: values.recipientName,
        email: values.recipientEmail,
      };
      const renderedSubject = renderCampaignTemplate(subject, mergeRecipient);
      const renderedBody = renderCampaignTemplate(body, mergeRecipient);

      // 3. [TEST] prefix + approximate footer (inert unsubscribe).
      const footer = buildTestFooterHtml({
        physicalAddress: senderExtras.address,
        signature: senderExtras.signature,
      });

      // 4. Send via the existing client direct path; isTest skips CRM logging.
      await sendEmail({
        providerId: resolvedSenderId,
        to: values.recipientEmail,
        subject: `[TEST] ${renderedSubject}`,
        body: renderedBody + footer,
        isTest: true,
      });

      // 5. Record in the ledger for rate-limiting.
      const { error: recordError } = await supabase.rpc('record_test_send', {
        p_workspace_id: workspaceId,
        p_recipient_email: values.recipientEmail,
      });
      if (recordError) {
        // Non-fatal: the email already sent. Log but don't fail the flow.
        console.error('Error recording test send:', recordError);
        captureError(recordError as Error, { feature: 'send-test-email' });
      }

      toast({
        title: 'Test email sent',
        description: `Sent to ${values.recipientEmail}.`,
      });
      form.reset();
      onOpenChange(false);
    } catch (err) {
      console.error('Error sending test email:', err);
      if (!showReconnectErrorToast(err, senderProvider.type)) {
        captureError(err as Error, { feature: 'send-test-email' });
        toast({
          title: 'Failed to send test email',
          description: err instanceof Error ? err.message : 'Something went wrong.',
          variant: 'destructive',
        });
      }
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Send Test Email</DialogTitle>
          <DialogDescription>
            Send a one-off copy of this email to yourself or a teammate to preview how it looks.
            Merge fields are filled from the name below.
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
            {showSenderPicker && (
              <FormItem>
                <FormLabel>From</FormLabel>
                <SenderSelect
                  value={pickedSenderId}
                  onValueChange={setPickedSenderId}
                  disabled={noAccounts || sending}
                />
                {noAccounts && (
                  <p className="text-sm text-destructive">
                    Connect an email account first to send a test.
                  </p>
                )}
              </FormItem>
            )}

            <FormField
              control={form.control}
              name="recipientName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Recipient name</FormLabel>
                  <FormControl>
                    <Input {...field} placeholder="Jane Doe" disabled={sending} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="recipientEmail"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Recipient email</FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      type="email"
                      placeholder="jane@example.com"
                      disabled={sending}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <p className="text-xs text-muted-foreground">
              This is a test send. The email includes an Unsubscribe link for layout preview, but
              it's inactive — it won't unsubscribe the recipient.
            </p>

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={sending}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={sending || noAccounts || !resolvedSenderId}>
                {sending ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    <Send className="h-4 w-4 mr-1" />
                    Send Test Email
                  </>
                )}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
